import { memo, useContext } from "react";
import { Button, ButtonGroup, Chip, Box, Paper } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  NotificationsActive as ActiveIcon,
  History as HistoryIcon,
  CheckCircle as AckIcon,
  Cancel as ClearIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

import {
  alarmFiltersSelector,
  openModal,
  selectedAlarmRows,
  temporaryAlarmFiltersSelector,
  updateAlarmFilters,
  updateTemporaryAlarmFilters,
  useGetGroupByCountQuery,
} from "./alarmsSlice";
import { ActiveAlarmsData } from "../../Components/Alarms/ActiveAlarms";
import { getAlarmSeverityLabel, getAlarmsQueryParams } from "./alarmUtils";

const AlarmStatusTabs = memo(() => {
  const dispatch = useDispatch();
  const temporaryFilters = useSelector(temporaryAlarmFiltersSelector);
  const alarmFilters = useSelector(alarmFiltersSelector);
  const alarmStatus = temporaryFilters ? temporaryFilters.is_active : alarmFilters.is_active;
  const handleAlarmFilterChange = (param) => {
    if (temporaryFilters) {
      dispatch(updateTemporaryAlarmFilters(param));
      return;
    }
    dispatch(updateAlarmFilters(param));
  };

  return (
    <ButtonGroup
      variant="outlined"
      aria-label="alarm status tabs"
      sx={{
        '& .MuiButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '0.5rem 1.25rem',
          borderColor: 'var(--primary-500)',
          gap: '0.5rem',
        },
        '& .MuiButton-contained': {
          backgroundColor: 'var(--primary-600)',
          '&:hover': {
            backgroundColor: 'var(--primary-700)',
          },
        },
        '& .MuiButton-outlined': {
          color: 'var(--primary-600)',
          '&:hover': {
            backgroundColor: 'var(--primary-50)',
          },
        },
      }}
    >
      <Button
        variant={alarmStatus === true ? "contained" : "outlined"}
        onClick={() => handleAlarmFilterChange({ is_active: true })}
        startIcon={<ActiveIcon sx={{ fontSize: 18 }} />}
      >
        Active
      </Button>
      <Button
        variant={alarmStatus === false ? "contained" : "outlined"}
        onClick={() => handleAlarmFilterChange({ is_active: false })}
        startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
      >
        History
      </Button>
    </ButtonGroup>
  );
});

const AlarmGroupByCount = memo(() => {
  const alarmFilters = useSelector(alarmFiltersSelector);
  const temporaryFilters = useSelector(temporaryAlarmFiltersSelector);
  const { customerIdList } = useContext(ActiveAlarmsData);
  const { data: groupByData } = useGetGroupByCountQuery(
    getAlarmsQueryParams({ temporaryFilters, customerIdList, alarmFilters }),
  );

  const getSeverityStyles = (colName) => {
    if (colName === "WARNING") {
      return {
        backgroundColor: 'var(--alarm-predictive-bg, #fef3c7)',
        color: 'var(--alarm-predictive-text, #b97400)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    }
    return {
      backgroundColor: 'var(--alarm-failure-bg, #fee2e2)',
      color: 'var(--alarm-failure-text, #b80000)',
      borderColor: 'rgba(239, 68, 68, 0.3)',
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    };
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {groupByData?.map((_type, index) => {
        const styles = getSeverityStyles(_type.col_name);
        return (
          <Chip
            key={`${_type.col_name}-${index}`}
            icon={styles.icon}
            label={`${getAlarmSeverityLabel(_type.col_name) ?? _type.col_name}: ${_type.cnt}`}
            size="small"
            sx={{
              backgroundColor: styles.backgroundColor,
              color: styles.color,
              border: `1px solid ${styles.borderColor}`,
              fontWeight: 600,
              fontSize: '0.8125rem',
              '& .MuiChip-icon': {
                color: styles.color,
              },
              '& .MuiChip-label': {
                padding: '0 0.5rem',
              },
            }}
          />
        );
      })}
    </Box>
  );
});

const AlarmActionsTab = () => {
  const dispatch = useDispatch();
  const selectedRows = useSelector(selectedAlarmRows);
  const handleAction = (type) => dispatch(openModal({ modalType: type, alarmId: selectedRows.join(",") }));

  if (selectedRows.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Chip
        label={`${selectedRows.length} selected`}
        size="small"
        sx={{
          backgroundColor: 'var(--primary-50)',
          color: 'var(--primary-700)',
          fontWeight: 500,
        }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleAction("multi_ack")}
        startIcon={<AckIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderColor: 'var(--success-main)',
          color: 'var(--success-dark)',
          '&:hover': {
            backgroundColor: 'var(--success-light)',
            borderColor: 'var(--success-main)',
          },
        }}
      >
        Acknowledge
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleAction("multi_clear")}
        startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderColor: 'var(--error-main)',
          color: 'var(--error-dark)',
          '&:hover': {
            backgroundColor: 'var(--error-light)',
            borderColor: 'var(--error-main)',
          },
        }}
      >
        Clear
      </Button>
    </Box>
  );
};

export const AlarmTableHeader = memo(({ allowUpdateAlarms }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-color)',
        marginBottom: '0.5rem',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <AlarmStatusTabs />
        <AlarmGroupByCount />
      </Box>
      {allowUpdateAlarms && <AlarmActionsTab />}
    </Paper>
  );
});
